/**
 * Per-app bearer tokens — manager view.
 *
 * Every signed-in user can mint their own tokens, but the standalone
 * Tokens page is reserved for managers: it pairs the user's own tokens
 * with an "All tokens on this app" admin section for offboarding and
 * audit. Non-managers see the same mint UI inlined into the AppDetail
 * overview, so this URL just redirects them back there.
 *
 * Path allowlist is read-only here: setting paths is done by the AI agent
 * via `kin_set_bearer_paths`. The portal surfaces the current list so
 * users know what their token can reach.
 */

import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';
import type { AppTokenSummary, AppTokenWithOwner, DeployTokenListResponse } from '../api/types.js';
import { AppTabs } from '../components/AppTabs.js';
import { DeployTokenSection } from '../components/DeployTokenSection.js';
import { UserTokenSection } from '../components/UserTokenSection.js';
import { IconExternal, IconTrash, IconX } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Mono,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

interface TokensPayload {
  myTokens: AppTokenSummary[];
  /** Only populated when the caller is a manager — null otherwise. */
  allTokens: AppTokenWithOwner[] | null;
  /** Only populated when the caller is a manager — null otherwise. */
  deployTokens: DeployTokenListResponse | null;
}

async function loadTokensPayload(slug: string): Promise<TokensPayload> {
  // Optimistically try the manager-scoped endpoints in parallel with
  // our own tokens. If either 403s (caller isn't a manager), fall back
  // to mine-only — `mineP` is already settled by then, so the fallback
  // is free. Doing this inside the loader (rather than in a useEffect
  // retry) avoids a re-render cycle and any risk of repeated retries.
  const mineP = api.get<{ tokens: AppTokenSummary[] }>(
    `/api/apps/${encodeURIComponent(slug)}/tokens`,
  );
  try {
    const [mine, all, deploy] = await Promise.all([
      mineP,
      api.get<{ tokens: AppTokenWithOwner[] }>(
        `/api/apps/${encodeURIComponent(slug)}/admin/tokens`,
      ),
      api.get<DeployTokenListResponse>(`/api/apps/${encodeURIComponent(slug)}/deploy-tokens`),
    ]);
    return { myTokens: mine.tokens, allTokens: all.tokens, deployTokens: deploy };
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      const mine = await mineP;
      return { myTokens: mine.tokens, allTokens: null, deployTokens: null };
    }
    throw err;
  }
}

export function Tokens() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload } = useAppPageWith(slug, loadTokensPayload);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [showManagerSection, setShowManagerSection] = useState(true);

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;

  // Non-managers manage their tokens inline on /apps/:slug now — send
  // them there rather than render a second surface with the same UI.
  if (!app.is_manager) return <Navigate to={`/apps/${app.slug}`} replace />;

  const appHost = app.cf_custom_domain_live ?? `${app.slug}.mkin.app`;
  const myTokens = payload.myTokens;
  const allTokens = payload.allTokens;
  const deployPayload = payload.deployTokens;

  async function revokeAsManager(t: AppTokenWithOwner) {
    if (!slug) return;
    const ownerLabel = t.owner.name ?? t.owner.email ?? t.owner.user_id;
    if (
      !confirm(
        `Revoke ${ownerLabel}'s token "${t.label}"? They'll lose programmatic access to this app immediately.`,
      )
    )
      return;
    await runAction({
      do: () => api.delete(`/api/apps/${encodeURIComponent(slug)}/admin/tokens/${t.id}`),
      onSuccess: () => `Revoked "${t.label}".`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Tokens"
        description={
          <>
            Mint a bearer token to call <Mono>{appHost}</Mono> from MCP clients, scripts, or other
            services. The browser cookie still works as before; tokens are for programmatic callers.
          </>
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          <UserTokenSection
            app={app}
            myTokens={myTokens}
            busy={busy}
            setBusy={setBusy}
            setActionMessage={setActionMessage}
            reload={reload}
          />

          {allTokens && allTokens.length > 0 && showManagerSection && (
            <Card>
              <CardHeader
                title="All tokens on this app"
                description="Every user's tokens, with owner. Revoke when offboarding."
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowManagerSection(false)}
                    aria-label="Collapse"
                  >
                    <IconX size={13} />
                  </Button>
                }
              />
              <ul className="divide-y divide-line">
                {allTokens.map((t) => (
                  <li key={t.id} className="px-5 py-3 text-sm">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Mono className="font-medium">{t.label}</Mono>
                          {t.revoked_at && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-danger-soft text-danger font-medium uppercase tracking-wide">
                              revoked
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted">
                          {t.owner.name ?? t.owner.email ?? t.owner.user_id} ·{' '}
                          {t.last_used_at ? `last used ${relTime(t.last_used_at)}` : 'never used'}
                          {' · '}
                          {t.revoked_at
                            ? `revoked ${relTime(t.revoked_at)}`
                            : `expires ${relTime(t.expires_at)}`}
                        </p>
                      </div>
                      {!t.revoked_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void revokeAsManager(t)}
                          disabled={busy}
                          className="!text-danger hover:!bg-danger-soft"
                        >
                          <IconTrash size={13} />
                          Revoke
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {deployPayload && (
            <div className="pt-2 border-t border-line">
              <div className="mb-4 mt-3 space-y-1">
                <h2 className="text-base font-semibold text-ink">Deploy tokens (CI)</h2>
                <p className="text-sm text-muted">
                  Outbound bearers for GitHub Actions to deploy this app. Different from the inbound
                  tokens above: deploy tokens call platform MCP, not the app.
                </p>
              </div>
              <DeployTokenSection
                slug={app.slug}
                payload={deployPayload}
                busy={busy}
                setBusy={setBusy}
                setActionMessage={setActionMessage}
                reload={reload}
              />
            </div>
          )}

          <p className="text-xs text-muted">
            Looking for help wiring Claude Code?{' '}
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/mcp"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ink transition-colors inline-flex items-center gap-0.5"
            >
              MCP setup docs <IconExternal size={11} />
            </a>
          </p>
        </main>
      </div>
    </div>
  );
}
